import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ConfigProvider, Spin } from 'antd';
import ukUA from 'antd/locale/uk_UA';
import { QueryClient, QueryClientProvider } from 'react-query';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { useNovaPoshtaConfigured } from './hooks/useNovaPoshtaConfigured';
import { AdminRole } from './types';
import AdminLayout from './components/AdminLayout';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './pages/Login';
import McpAccess from './pages/McpAccess';
import OAuthConsent from './pages/OAuthConsent';
import styles from './App.module.css';
import './styles/mobile.css';
import './styles/responsive.css';

// Lazy load pages for better performance
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Products = lazy(() => import('./pages/Products'));
const Categories = lazy(() => import('./pages/Categories'));
const Orders = lazy(() => import('./pages/Orders'));
const NovaPoshtaDispatch = lazy(() => import('./pages/NovaPoshtaDispatch'));
const ManualOrder = lazy(() => import('./pages/ManualOrder'));
const OrderEdit = lazy(() => import('./pages/OrderEdit'));
const OrderVerification = lazy(() => import('./pages/OrderVerification'));
const Users = lazy(() => import('./pages/Users'));
const CustomerCard = lazy(() => import('./pages/CustomerCard'));
const Settings = lazy(() => import('./pages/Settings'));
const PickupLocationPage = lazy(() => import('./pages/settings/PickupLocationPage'));
const DeliverySchedulePage = lazy(() => import('./pages/settings/DeliverySchedulePage'));
const FAQ = lazy(() => import('./pages/FAQ'));
const LegalDocuments = lazy(() => import('./pages/LegalDocuments'));
const Recipes = lazy(() => import('./pages/Recipes'));
const RecipeEditor = lazy(() => import('./pages/recipes/RecipeEditor'));
const Supercategories = lazy(() => import('./pages/Supercategories'));
const CatalogOrder = lazy(() => import('./pages/CatalogOrderSimple'));
const ProductFeed = lazy(() => import('./pages/ProductFeed'));
const Communication = lazy(() => import('./pages/Communication'));
const Broadcast = lazy(() => import('./pages/Broadcast'));
const Surveys = lazy(() => import('./pages/Surveys'));
const SurveyDetail = lazy(() => import('./pages/SurveyDetail'));
const Reviews = lazy(() => import('./pages/Reviews'));
const ProductRequests = lazy(() => import('./pages/ProductRequests'));
const AdminUsers = lazy(() => import('./pages/AdminUsers'));
const ProductStatistics = lazy(() => import('./pages/ProductStatistics'));
const Profit = lazy(() => import('./pages/Profit'));
const Finance = lazy(() => import('./pages/Finance'));
const Payments = lazy(() => import('./pages/Payments'));
const Reports = lazy(() => import('./pages/Reports'));
const UserStatistics = lazy(() => import('./pages/UserStatistics'));
const Campaigns = lazy(() => import('./pages/Campaigns'));
const Promotions = lazy(() => import('./pages/Promotions'));
const Warehouse = lazy(() => import('./pages/Warehouse'));
const Deliveries = lazy(() => import('./pages/Deliveries'));
const Shelves = lazy(() => import('./pages/Shelves'));
const AbandonedCarts = lazy(() => import('./pages/AbandonedCarts'));
const OKR = lazy(() => import('./pages/OKR'));
const AiAssistantPage = lazy(() => import('./pages/AiAssistantPage'));
const OrgTeam = lazy(() => import('./pages/OrgTeam'));
const OrgSettings = lazy(() => import('./pages/OrgSettings'));
const Branding = lazy(() => import('./pages/Branding'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Protected Route component with optional role-based access
const ProtectedRoute: React.FC<{ children: React.ReactNode; allowedRoles?: AdminRole[]; requirePermission?: string }> = ({ children, allowedRoles, requirePermission }) => {
  const { isAuthenticated, loading, role, hasPermission } = useAuth();
  const attemptedLocation = useLocation();

  if (loading) {
    return (
      <div className={styles.loadingScreen}>
        <Spin size="large" aria-label="loading" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Carry where they were going, so a flow that starts elsewhere (an MCP
    // client sending them here to approve a connection) resumes after login
    // instead of dumping them on the dashboard.
    return <Navigate to="/login" replace state={{ from: attemptedLocation }} />;
  }

  // Capability gate (same model as the sidebar / admin_agent.access): platform
  // superadmins bypass permission checks in hasPermission, so a permission that
  // is granted to nobody (e.g. okr.view) resolves to superadmin-only. This
  // stays correct in the multi-org model, where `role` is the active-org role
  // and never 'superadmin'.
  if (requirePermission && !hasPermission(requirePermission)) {
    return <Navigate to="/products" replace />;
  }

  if (allowedRoles && role && !allowedRoles.includes(role)) {
    return <Navigate to="/products" replace />;
  }

  return <>{children}</>;
};

// Role-aware default redirect
const DefaultRedirect: React.FC = () => {
  const { role } = useAuth();
  const target = role === 'ai_agent' ? '/products'
    : role === 'warehouse_manager' ? '/warehouse'
    : '/dashboard';
  return <Navigate to={target} replace />;
};

const ADMIN_SUPERADMIN: AdminRole[] = ['owner', 'admin', 'superadmin'];
const ORG_MANAGERS: AdminRole[] = ['owner', 'admin', 'superadmin'];

// Сторінка живе лише в магазина з ключем НП: без нього накладних не буває, і
// таблиця була б порожньою без пояснення. Поки прапорець їде — чекаємо, інакше
// прямий перехід за адресою викидав би й магазин, у якого ключ є.
const NovaPoshtaDispatchGuarded: React.FC = () => {
  const { configured: npConfigured, isLoading } = useNovaPoshtaConfigured();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !npConfigured) {
      navigate('/orders', { replace: true });
    }
  }, [isLoading, npConfigured, navigate]);

  if (isLoading) return <Spin size="large" />;
  if (!npConfigured) return null;

  return <NovaPoshtaDispatch />;
};

// Main App Routes
const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route
        path="/login"
        element={<Login />}
      />

      <Route path="/" element={<ProtectedRoute><DefaultRedirect /></ProtectedRoute>} />
      {/* Approval step for an MCP client. Guarded, so signing in happens on the
          normal login form and the person lands back here. */}
      <Route
        path="/mcp"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <Suspense fallback={<Spin size="large" />}>
                <McpAccess />
              </Suspense>
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/oauth/consent"
        element={<ProtectedRoute><OAuthConsent /></ProtectedRoute>}
      />
      
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <Dashboard />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/org/team"
        element={
          <ProtectedRoute allowedRoles={ORG_MANAGERS}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <OrgTeam />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      {/* Профіль компанії став розділом «Налаштувань магазину»; старі посилання
          й закладки ведуть просто на потрібну вкладку. */}
      <Route path="/system/profile" element={<Navigate to="/settings?tab=company" replace />} />
      {/* Old route kept for bookmarks → redirect to the new location. */}
      <Route path="/org/settings" element={<Navigate to="/system/profile" replace />} />

      {/* Бренд і тексти став розділом «Налаштувань магазину»; старі посилання
          й закладки ведуть просто на потрібну вкладку. */}
      <Route path="/branding" element={<Navigate to="/settings?tab=branding" replace />} />

      
      <Route
        path="/products"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <Products />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/catalog-order"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <CatalogOrder />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/product-feed"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <ProductFeed />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/categories"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <Suspense fallback={<Spin size="large" />}>
                <Categories />
              </Suspense>
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/supercategories"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <Suspense fallback={<Spin size="large" />}>
                <Supercategories />
              </Suspense>
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/orders"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <Orders />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/orders/new"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <ManualOrder />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      {/* Сторінка замовлення. Статичні /orders/new і /orders/verification мають
          вищий пріоритет у v6; нечисловий :id Orders переадресує на список. */}
      <Route
        path="/orders/:id"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <Orders />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/orders/:id/edit"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <OrderEdit />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/orders/verification"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <OrderVerification />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/orders/verify/:id"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <Suspense fallback={<Spin size="large" />}>
                <OrderVerification />
              </Suspense>
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/users"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <Suspense fallback={<Spin size="large" />}>
                <Users />
              </Suspense>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/users/:id"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <Suspense fallback={<Spin size="large" />}>
                <CustomerCard />
              </Suspense>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <Suspense fallback={<Spin size="large" />}>
                <Settings />
              </Suspense>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/pickup-location"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <PickupLocationPage />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings/delivery-schedule"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <DeliverySchedulePage />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/faq"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <FAQ />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/recipes"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <Recipes />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/recipes/:id"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <RecipeEditor />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/legal"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <LegalDocuments />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />


      <Route
        path="/communication"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <Communication />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/broadcast"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <Broadcast />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/surveys"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <Surveys />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/survey/:id"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <SurveyDetail />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/reviews"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <Reviews />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/product-requests"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <ProductRequests />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/np-dispatch"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <NovaPoshtaDispatchGuarded />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/abandoned-carts"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <AbandonedCarts />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/statistics"
        element={<ProtectedRoute><Navigate to="/statistics/products" replace /></ProtectedRoute>}
      />

      <Route
        path="/statistics/products"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <ProductStatistics />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/statistics/users"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <UserStatistics />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/statistics/profit"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <Profit />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/finance"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <Finance />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/payments"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <Payments />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/reports"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <Reports />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/campaigns"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <Campaigns />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/promotions"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <Promotions />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin-users"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <AdminUsers />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/warehouse"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <Warehouse />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/deliveries"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <Deliveries />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/shelves"
        element={
          <ProtectedRoute allowedRoles={ADMIN_SUPERADMIN}>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <Shelves />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/okr"
        element={
          <ProtectedRoute requirePermission="okr.view">
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <OKR />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/ai"
        element={
          <ProtectedRoute>
            <AdminLayout>
              <ErrorBoundary>
                <Suspense fallback={<Spin size="large" />}>
                  <AiAssistantPage />
                </Suspense>
              </ErrorBoundary>
            </AdminLayout>
          </ProtectedRoute>
        }
      />

      <Route path="*" element={<ProtectedRoute><DefaultRedirect /></ProtectedRoute>} />
    </Routes>
  );
};

// App component without router for testing
const AppContent: React.FC = () => {
  return (
    <ErrorBoundary>
      <ConfigProvider
        locale={ukUA}
        theme={{
          token: {
            colorPrimary: '#ff5f6d',
            colorLink: '#ff5f6d',
            colorInfo: '#ff5f6d',
            borderRadius: 8,
            fontFamily: "'Segoe UI', system-ui, -apple-system, 'Helvetica Neue', sans-serif",
          },
        }}
      >
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </QueryClientProvider>
      </ConfigProvider>
    </ErrorBoundary>
  );
};

// Main App with router for production
const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
};

export { AppContent };
export default App;